import React, { useEffect, useState } from 'react';
import { Calendar, ChevronDown, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { getHomepageSectionML } from '../../services/api';
import { Link } from 'react-router-dom';

const HeroSectionML = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadHeroContent(); }, [language]);

  const loadHeroContent = async () => {
    setLoading(true);
    const normalizedLang = ['pt','en','fr','ar','zh'].includes(language) ? language : (language.startsWith('pt') ? 'pt' : 'en');
    const result = await getHomepageSectionML('hero', normalizedLang);
    if (result.success) setContent(result.data);
    setLoading(false);
  };

  const scrollToNext = () => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });

  if (loading || !content) {
    return <section className="relative min-h-screen flex items-center justify-center bg-[#0a0a0a]"><div className="text-white">Loading...</div></section>;
  }

  const bgImage = content.background_image;
  const bgVideo = content.background_video;
  const overlayOpacity = content.overlay_opacity != null ? content.overlay_opacity : 30;
  const brightness = content.image_brightness != null ? content.image_brightness : 100;

  return (
    <section className="relative min-h-screen flex items-center justify-center text-white overflow-hidden bg-[#0a0a0a]">
      {bgVideo ? (
        <>
          {/* Institutional video loop — autoplays muted, loops seamlessly */}
          <video
            key={bgVideo}
            src={bgVideo}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            // Poster acts as the fallback image before the video buffers or if autoplay is blocked
            poster={bgImage || undefined}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: `brightness(${brightness / 100})` }}
            data-testid="hero-bg-video"
          />
          <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
        </>
      ) : bgImage ? (
        <>
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${bgImage})`, filter: `brightness(${brightness / 100})` }} />
          <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
        </>
      ) : (
        <div className="absolute inset-0 bg-[#023E8A]">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
      )}

      <div className="container mx-auto px-6 py-32 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Pre-headline with separator line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-12 bg-[#48CAE4]" />
            <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[#48CAE4] drop-shadow-lg">
              {content.preheadline}
            </p>
            <div className="h-px w-12 bg-[#48CAE4]" />
          </div>

          {/* Main Headline — Bold, Large, Impactful */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.1] mb-8 tracking-tight drop-shadow-xl">
            {content.headline}
          </h1>

          {/* Subheadline — Lighter, Readable */}
          <p className="text-base md:text-lg lg:text-xl text-white/85 leading-relaxed max-w-3xl mx-auto mb-14 font-light drop-shadow-md">
            {content.subheadline}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link to={content.button_primary_link || '/schedule'}>
              <Button size="lg" className="bg-[#FF6B35] hover:bg-[#E5562D] text-white font-bold px-10 py-7 text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 rounded-xl">
                <Calendar className="mr-3 h-5 w-5" />
                {content.button_primary_text}
              </Button>
            </Link>
            <Link to={content.button_secondary_link || '/services'}>
              <Button size="lg" variant="outline" className="border-2 border-white/80 text-white hover:bg-white hover:text-[#023E8A] font-semibold px-10 py-7 text-lg transition-all duration-300 hover:scale-105 rounded-xl backdrop-blur-sm">
                {content.button_secondary_text}
              </Button>
            </Link>
          </div>

          {/* Trust Indicator */}
          <div className="flex items-center justify-center gap-3 text-sm text-white/70">
            <ShieldCheck className="h-5 w-5 text-[#48CAE4]" />
            <span className="font-medium tracking-wide">{content.trust_indicator}</span>
          </div>
        </div>
      </div>

      <button onClick={scrollToNext} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 hover:text-white transition-colors animate-bounce z-10" aria-label="Scroll">
        <ChevronDown className="h-8 w-8" />
      </button>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/30 to-transparent pointer-events-none z-[5]" />
    </section>
  );
};

export default HeroSectionML;
